import { forwardRef, useId, type FormEvent, type KeyboardEvent } from 'react';
import { Button } from '../atoms/Button/Button';
import { Checkbox } from '../atoms/Checkbox/Checkbox';
import { Textarea } from '../atoms/Textarea/Textarea';
import { useTranslation } from '../../i18n/useTranslation';
import { MENTOR_MAX_MESSAGE_LENGTH } from '../../services/mentorService';
import './Mentor.css';

export interface MentorComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  includePhotos: boolean;
  onIncludePhotosChange: (value: boolean) => void;
  /** A question is being answered. */
  busy: boolean;
  /** Cancels the answer being written (shown as "Cancel" while busy). */
  onCancel?: () => void;
  /** Sending is not possible (e.g. AI credits used up). */
  disabled?: boolean;
}

/**
 * Question input of the mentor chat: labelled Textarea (Enter sends,
 * Shift+Enter makes a new line, IME composition is respected), the
 * "include newest gallery photos" choice, the "Send" button (disabled while an
 * answer is being written) and, while busy, "Cancel".
 */
export const MentorComposer = forwardRef<HTMLTextAreaElement, MentorComposerProps>(function MentorComposer(
  { value, onChange, onSubmit, includePhotos, onIncludePhotosChange, busy, onCancel, disabled = false },
  ref,
) {
  const { t } = useTranslation();
  const textareaId = useId();
  const canSend = value.trim() !== '' && !busy && !disabled;

  const submit = (event?: FormEvent): void => {
    event?.preventDefault();
    if (canSend) onSubmit();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    submit();
  };

  return (
    <form className="mentor-composer" onSubmit={submit} aria-label={t('mentor.composer.formLabel')}>
      <Textarea
        ref={ref}
        id={textareaId}
        label={t('mentor.composer.label')}
        helperText={t('mentor.composer.hint', { max: MENTOR_MAX_MESSAGE_LENGTH })}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        maxLength={MENTOR_MAX_MESSAGE_LENGTH}
        rows={3}
        fullWidth
        disabled={disabled}
        data-testid="mentor-input"
      />
      <div className="mentor-composer__row">
        <Checkbox
          label={t('mentor.composer.includePhotos')}
          checked={includePhotos}
          onChange={(e) => onIncludePhotosChange(e.target.checked)}
          disabled={disabled}
        />
        <div className="mentor-composer__actions">
          {busy && onCancel && (
            <Button type="button" variant="secondary" onClick={onCancel} data-testid="mentor-cancel">
              {t('common.cancel')}
            </Button>
          )}
          <Button type="submit" variant="primary" isLoading={busy} disabled={!canSend} data-testid="mentor-send">
            {busy ? t('mentor.composer.sending') : t('mentor.composer.send')}
          </Button>
        </div>
      </div>
    </form>
  );
});
